import React from "react";
import { css } from "@emotion/css";
import { useNavigate } from "react-router-dom";
import { MdCreditCard, MdVerifiedUser, MdHelp } from "react-icons/md";

// Import payment icons
import MercadoPagoIcon from "../../../assets/card-brans/mp_handshake.svg";
import PixIcon from "../../../assets/payment-methods/pix.svg";
import VisaIcon from "../../../assets/card-brans/visa.svg";
import MastercardIcon from "../../../assets/card-brans/mastercard.svg";
import AmexIcon from "../../../assets/card-brans/amex.svg";
import EloIcon from "../../../assets/card-brans/elo.svg";
import HiperIcon from "../../../assets/card-brans/hiper.svg";

// Import security badges
import GoogleSafeBrowser from "../../../assets/icons/google-safe-browser.webp";
import PCICompliant from "../../../assets/icons/PCI.webp";

export interface PaymentSecurityFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  onContactClick?: () => void;
}

const footerInfo = css({
  width: "100%",
  margin: "0 auto 4rem",
});

const footerSection = css({
  backgroundColor: "var(--primary-color)",
  borderRadius: "var(--spacing-md)",
  boxShadow: "var(--shadow-300)",
  padding: "2rem",
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "2rem",
  border: "1px solid rgba(var(--pcrgba-200), 0.5)",
});

const footerTitle = css({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "var(--pc-700)",
  borderBottom: "2px solid rgba(var(--pcrgba-200), 0.5)",
  textAlign: "center",
  lineHeight: 1.2,
});

const paymentSection = css({
  flex: 1,
  minWidth: "15.625rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  alignSelf: "flex-start",
});

const sectionHeader = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-sm)",
  marginBottom: "1rem",
  justifyContent: "center",
  width: "100%",
});

const sectionIcon = css({
  color: "var(--pc-700)",
  minWidth: "20px",
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const paymentIcons = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "0.75rem",
  alignItems: "center",
  justifyContent: "center",
});

const paymentIcon = css({
  height: "1.75rem",
  width: "auto",
  transition: "transform 0.2s ease",
  filter: "grayscale(20%)",
  opacity: 0.9,
  
  "&:hover": {
    transform: "translateY(-3px)",
    filter: "grayscale(0%)",
    opacity: 1,
  },
  
  "&.mercadopago-icon": {
    height: "2.125rem",
  },
  
  "&.card-icon": {
    height: "1.5rem",
  },
});

const securitySection = css({
  flex: 1,
  minWidth: "15.625rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  alignSelf: "flex-start",
});

const securityBadges = css({
  display: "flex",
  gap: "1.5rem",
  alignItems: "center",
  justifyContent: "center",
});

const securityBadge = css({
  height: "3rem",
  width: "auto",
  transition: "transform 0.2s ease",
  filter: "grayscale(10%)",
  opacity: 0.95,
  
  "&:hover": {
    transform: "translateY(-3px)",
    filter: "grayscale(0%)",
    opacity: 1,
  },
});

const helpSection = css({
  flex: 1,
  minWidth: "15.625rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  alignSelf: "flex-start",
});

const contactButton = css({
  display: "flex !important",
  alignItems: "center !important",
  gap: "0.5rem !important",
  backgroundColor: "var(--pc-600) !important",
  color: "white !important",
  border: "none !important",
  padding: "0.75rem 1.5rem !important",
  borderRadius: "var(--spacing-sm) !important",
  fontWeight: 500,
  fontSize: "1rem !important",
  width: "fit-content",
  cursor: "pointer",
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: "var(--pc-700) !important",
    boxShadow: "0 6px 15px rgba(var(--pcrgba-600), 0.3)",
  },
});

export const PaymentSecurityFooter: React.FC<PaymentSecurityFooterProps> = ({
  onContactClick,
  className,
  ...rest
}) => {
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    if (onContactClick) {
      onContactClick();
    } else {
      navigate('/contato');
    }
  };
  
  return (
    <div className={`${footerInfo} ${className || ''}`} {...rest}>
      <div className={footerSection}>
        <div className={paymentSection}>
          <div className={sectionHeader}>
            <MdCreditCard className={sectionIcon} />
            <h3 className={footerTitle}>Formas de Pagamento</h3>
          </div>
          <div className={paymentIcons}>
            <img 
              src={MercadoPagoIcon} 
              alt="Mercado Pago" 
              className={`${paymentIcon} mercadopago-icon`} 
            />
            <img 
              src={PixIcon} 
              alt="Pix" 
              className={paymentIcon} 
            />
            <img 
              src={VisaIcon} 
              alt="Visa" 
              className={`${paymentIcon} card-icon`} 
            />
            <img 
              src={MastercardIcon} 
              alt="Mastercard" 
              className={`${paymentIcon} card-icon`} 
            />
            <img 
              src={AmexIcon} 
              alt="American Express" 
              className={`${paymentIcon} card-icon`} 
            />
            <img 
              src={EloIcon} 
              alt="Elo" 
              className={`${paymentIcon} card-icon`} 
            />
            <img 
              src={HiperIcon} 
              alt="Hiper" 
              className={`${paymentIcon} card-icon`} 
            />
          </div>
        </div>
        
        <div className={securitySection}>
          <div className={sectionHeader}>
            <MdVerifiedUser className={sectionIcon} />
            <h3 className={footerTitle}>Compre com Total Segurança</h3>
          </div>
          <div className={securityBadges}>
            <img 
              src={GoogleSafeBrowser} 
              alt="Google Safe Browser" 
              className={securityBadge} 
            />
            <img 
              src={PCICompliant} 
              alt="PCI Compliant" 
              className={securityBadge} 
            />
          </div>
        </div>
        
        <div className={helpSection}>
          <div className={sectionHeader}>
            <MdHelp className={sectionIcon} />
            <h3 className={footerTitle}>Precisa de Ajuda?</h3>
          </div>
          <button 
            onClick={handleContactClick}
            className={contactButton}
          >
            <MdHelp size={16} />
            Fale Conosco
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSecurityFooter;
